// Insurance tab rebuild — claims list card. Rows open the claim drawer;
// "File a claim" (gated Insurance.Verify) opens the FileClaimModal.

import { useState } from "react";
import { Plus } from "lucide-react";
import { Badge, Button, Card, SkeletonList } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useInsuranceClaims } from "@/hooks/portfolioAdmin/useInsuranceClaims";
import { formatCalendarDate } from "@/design-system/lib/format";
import { CLAIM_STATUS_BADGE, formatClaimAmount } from "./claimLifecycle";
import { FileClaimModal } from "./FileClaimModal";
import { ClaimDrawer } from "./ClaimDrawer";

interface ClaimsCardProps {
  loanId: string;
}

export function ClaimsCard({ loanId }: ClaimsCardProps) {
  const auth = useAuth();
  const canFile =
    auth.hasPermission("Insurance.Verify") || auth.hasRoleCode("ADMIN");

  const { data, isLoading, isError, refetch } = useInsuranceClaims(loanId);
  const claims = data ?? [];

  const [fileOpen, setFileOpen] = useState(false);
  const [openClaimId, setOpenClaimId] = useState<string | null>(null);
  const openClaim = openClaimId
    ? claims.find((c) => c.id === openClaimId) ?? null
    : null;

  return (
    <Card data-testid="insurance-claims-card">
      <div className="flex items-center justify-between gap-space-3 mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">Claims</h3>
        {canFile && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setFileOpen(true)}
            data-testid="claims-file-button"
          >
            <Plus size={14} aria-hidden /> File a claim
          </Button>
        )}
      </div>

      {isLoading ? (
        <SkeletonList rows={3} data-testid="claims-loading" />
      ) : isError ? (
        <div className="flex items-center gap-space-3" data-testid="claims-error">
          <p className="text-small text-status-error m-0">
            Could not load claims.
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : claims.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0" data-testid="claims-empty">
          No claims filed on this loan.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col divide-y divide-border-light">
          {claims.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setOpenClaimId(c.id)}
                className="w-full flex items-center gap-space-3 py-space-2 text-left hover:bg-white-warm transition-colors"
                data-testid={`claim-row-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-small text-onyx font-display font-semibold">
                    {c.policyType}
                    {c.claimNumber && (
                      <span className="ml-space-2 font-mono font-normal text-caption text-gray-mid">
                        #{c.claimNumber}
                      </span>
                    )}
                  </div>
                  <div className="text-caption text-gray-mid">
                    Loss {formatCalendarDate(c.dateOfLoss)}
                  </div>
                </div>
                <div className="shrink-0 text-right font-mono text-small text-onyx">
                  <div>{formatClaimAmount(c.amountClaimed)}</div>
                  {c.amountSettled != null && (
                    <div className="text-caption text-gray-mid">
                      Settled {formatClaimAmount(c.amountSettled)}
                    </div>
                  )}
                </div>
                <Badge status={CLAIM_STATUS_BADGE[c.status]} className="shrink-0">
                  {c.status}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
      )}

      {fileOpen && (
        <FileClaimModal
          loanId={loanId}
          open={fileOpen}
          onClose={() => setFileOpen(false)}
        />
      )}
      {openClaim && (
        <ClaimDrawer
          loanId={loanId}
          claim={openClaim}
          open={!!openClaim}
          onClose={() => setOpenClaimId(null)}
        />
      )}
    </Card>
  );
}
